import isClassDiaryDemo from "../../demo/isClassDiaryDemo";
import i18n from "../../utils/i18n";
import {
  attachMockVoiceSamples,
  buildSpeakersFromAta,
  listPersonOptions,
  MIN_VOICE_SAMPLES,
  normalizeAttributedTranscript
} from "./speakers";

/* Funções de sync referenciam exports definidos mais abaixo no módulo. */
/* eslint-disable no-use-before-define, no-console */

const STORAGE_KEY = "ataInteligenteDrafts";
const DEMO_PURGED_KEY = "ataInteligenteDemoPurged_v2";

/**
 * Tombstones: impede que persist/upsert em voo “ressuscite” ata recém-excluída
 * (mesmo clientDraftId → novo meeting id no Postgres).
 */
const deletedDraftKeys = new Set();

function draftTombstoneKeys(id, meetingId) {
  const keys = [];
  if (id != null && String(id).trim() !== "") keys.push(String(id));
  if (meetingId != null && String(meetingId).trim() !== "") {
    keys.push(`m:${String(meetingId)}`);
  }
  return keys;
}

export function markAtaDraftDeleted(id, meetingId) {
  draftTombstoneKeys(id, meetingId).forEach(k => deletedDraftKeys.add(k));
}

export function isAtaDraftDeleted(id, meetingId) {
  if (id != null && deletedDraftKeys.has(String(id))) return true;
  if (meetingId != null && deletedDraftKeys.has(`m:${String(meetingId)}`)) {
    return true;
  }
  return false;
}

/** Helpers Alice/fixture só em demo ou quando o pipeline mock devolveu fixture. */
function allowAliceFixtureHelpers(response) {
  if (isClassDiaryDemo()) return true;
  if (!response) return false;
  if (response.provider === "mock") return true;
  if (response.fixtureKey) return true;
  return false;
}

function mergeRemoteIntoLocal(remoteList) {
  const local = listAtaDrafts().filter(
    d => d && !isAtaDraftDeleted(d.id, d.meetingId)
  );
  const byId = new Map(local.map(d => [d.id, d]));
  (remoteList || []).forEach(remote => {
    if (!remote || !remote.id) return;
    if (isAtaDraftDeleted(remote.id, remote.meetingId)) return;
    const prev = byId.get(remote.id) || {};
    byId.set(remote.id, {
      ...prev,
      ...remote,
      // Áudio local (IndexedDB) não vem do server
      hasLocalAudio:
        prev.hasLocalAudio === true || remote.hasLocalAudio === true
    });
  });
  const merged = Array.from(byId.values()).sort((a, b) => {
    const ta = new Date(a.meetingAt || a.createdAt || 0).getTime();
    const tb = new Date(b.meetingAt || b.createdAt || 0).getTime();
    return tb - ta;
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged.slice(0, 80)));
  return merged;
}

/**
 * Busca atas no servidor e mescla no cache local (não-demo).
 */
export async function refreshAtaDraftsFromServer({ classId, studentRa } = {}) {
  if (isClassDiaryDemo()) {
    return filterAtasForContext(listAtaDrafts(), { classId, studentRa });
  }
  try {
    // Lazy: evita ciclo de import com SmartMinutesService
    const { default: SmartMinutesService } = await import(
      "../../services/SmartMinutesService"
    );
    const remote = await SmartMinutesService.listDrafts({
      classId,
      studentRa
    });
    const list = Array.isArray(remote) ? remote : [];
    mergeRemoteIntoLocal(list);
    return filterAtasForContext(listAtaDrafts(), { classId, studentRa });
  } catch (e) {
    console.warn(
      "[ata] refresh servidor falhou — usando cache local",
      e && e.message
    );
    return filterAtasForContext(listAtaDrafts(), { classId, studentRa });
  }
}

async function persistDraftToServer(draft) {
  if (isClassDiaryDemo() || !draft || !draft.id) return draft;
  if (isAtaDraftDeleted(draft.id, draft.meetingId)) return null;
  try {
    const { default: SmartMinutesService } = await import(
      "../../services/SmartMinutesService"
    );
    // Nunca envia o placeholder de “processando” como transcriptText ao servidor
    // (senão o pipeline pula o STT).
    const payload = { ...draft };
    const note = String(payload.transcriptText || "");
    if (
      /Processando áudio|Processing audio|transcrição será preenchida|transcript will be filled/i.test(
        note
      )
    ) {
      delete payload.transcriptText;
    }
    const saved = await SmartMinutesService.upsertDraft(payload);
    // Delete pode ter ocorrido durante o await — desfaz ressurreição.
    if (
      isAtaDraftDeleted(draft.id, draft.meetingId) ||
      (saved && isAtaDraftDeleted(saved.id, saved.meetingId))
    ) {
      const killId =
        (saved && (saved.meetingId || saved.id)) ||
        draft.meetingId ||
        draft.id;
      try {
        await SmartMinutesService.deleteDraft(killId);
      } catch (undoErr) {
        console.warn(
          "[ata] undo ressurreição falhou",
          undoErr && undoErr.message
        );
      }
      deleteAtaDraft(draft.id);
      if (saved && saved.id && saved.id !== draft.id) {
        deleteAtaDraft(saved.id);
      }
      return null;
    }
    if (saved && saved.meetingId) {
      const current = getAtaDraft(draft.id);
      if (current && current.meetingId !== saved.meetingId) {
        // Atualiza só meetingId sem re-persistir em loop
        const all = listAtaDrafts().map(d =>
          d.id === draft.id
            ? { ...d, meetingId: saved.meetingId, updatedAt: saved.updatedAt }
            : d
        );
        localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
      }
    }
    return saved || draft;
  } catch (e) {
    console.warn("[ata] persist servidor falhou", e && e.message);
    return draft;
  }
}

export async function deleteAtaDraftRemote(id, meetingId) {
  markAtaDraftDeleted(id, meetingId);
  deleteAtaDraft(id);
  if (isClassDiaryDemo() || (!id && !meetingId)) return true;
  const { default: SmartMinutesService } = await import(
    "../../services/SmartMinutesService"
  );
  // Preferir PK numérica; fallback no clientDraftId (ata_…).
  const keys = [];
  if (meetingId != null && String(meetingId).trim() !== "") {
    keys.push(String(meetingId));
  }
  if (id != null && String(id) !== String(meetingId)) {
    keys.push(String(id));
  }
  let lastErr = null;
  const tryDeleteKey = async index => {
    if (index >= keys.length) {
      if (lastErr) throw lastErr;
      return true;
    }
    try {
      await SmartMinutesService.deleteDraft(keys[index]);
      return true;
    } catch (e) {
      lastErr = e;
      console.warn("[ata] delete servidor falhou", keys[index], e && e.message);
      return tryDeleteKey(index + 1);
    }
  };
  return tryDeleteKey(0);
}

/**
 * Garante upsert no servidor (aguarda). Em demo, só retorna o draft local.
 */
export async function syncAtaDraftToServer(idOrDraft) {
  const draft =
    typeof idOrDraft === "string" ? getAtaDraft(idOrDraft) : idOrDraft;
  if (!draft) return null;
  return persistDraftToServer(draft);
}

/**
 * Busca um draft no servidor e mescla no cache local (não-demo).
 */
export async function fetchAtaDraftFromServer(id) {
  if (!id || isClassDiaryDemo()) return getAtaDraft(id);
  try {
    const { default: SmartMinutesService } = await import(
      "../../services/SmartMinutesService"
    );
    const remote = await SmartMinutesService.getDraft(id);
    if (!remote || !remote.id) return getAtaDraft(id);
    const prev = getAtaDraft(remote.id) || {};
    const merged = {
      ...prev,
      ...remote,
      hasLocalAudio:
        prev.hasLocalAudio === true || remote.hasLocalAudio === true
    };
    const all = listAtaDrafts();
    const idx = all.findIndex(d => d.id === merged.id);
    const next =
      idx >= 0
        ? all.map((d, i) => (i === idx ? merged : d))
        : [merged, ...all].slice(0, 80);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return merged;
  } catch (e) {
    console.warn("[ata] get servidor falhou", e && e.message);
    return getAtaDraft(id);
  }
}
/* eslint-enable no-use-before-define, no-console */

/** Status do ciclo de vida da ata (protótipo). */
export const ATA_STATUS = {
  RASCUNHO: "rascunho",
  /** STT + falantes/amostras — encaminhamentos ainda não liberados */
  VOZ_PROCESSADA: "voz_processada",
  PROCESSADA_IA: "processada_ia",
  APROVADA: "aprovada"
};

const ATA_STATUS_I18N = {
  [ATA_STATUS.RASCUNHO]: "smartMinutes.status.draft",
  [ATA_STATUS.VOZ_PROCESSADA]: "smartMinutes.status.voiceProcessed",
  [ATA_STATUS.PROCESSADA_IA]: "smartMinutes.status.aiProcessed",
  [ATA_STATUS.APROVADA]: "smartMinutes.status.approved"
};

/** @deprecated use getAtaStatusLabel */
export const ATA_STATUS_LABEL = {};

export function getAtaStatusLabel(status) {
  const key = ATA_STATUS_I18N[status];
  return key ? i18n.t(key) : status;
}

/**
 * Status efetivo para UI/persistência.
 * Corrige drafts travados em "rascunho" após processar (transcript/actions/IA).
 */
export function resolveAtaStatus(ata) {
  if (!ata) return ATA_STATUS.RASCUNHO;
  const raw = ata.status || ATA_STATUS.RASCUNHO;
  if (raw === ATA_STATUS.APROVADA) return ATA_STATUS.APROVADA;
  if (raw === ATA_STATUS.PROCESSADA_IA) return ATA_STATUS.PROCESSADA_IA;

  const hasActions =
    ata.actionsReady === true ||
    (Array.isArray(ata.actions) && ata.actions.length > 0);
  const hasAiEvidence =
    ata.processedByAi === true ||
    !!ata.processAppliedJobId ||
    !!ata.meetingId ||
    !!(ata.transcriptText && String(ata.transcriptText).trim()) ||
    hasActions;

  if (raw === ATA_STATUS.VOZ_PROCESSADA) {
    return hasActions ? ATA_STATUS.PROCESSADA_IA : ATA_STATUS.VOZ_PROCESSADA;
  }

  if (!hasAiEvidence) return raw;

  return hasActions ? ATA_STATUS.PROCESSADA_IA : ATA_STATUS.VOZ_PROCESSADA;
}

function isDemoAtaId(id) {
  return String(id || "").startsWith("ata_demo_");
}

export function listAtaDrafts() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

export function saveAtaDraft(draft, { persist = true } = {}) {
  if (!draft || !draft.id) return draft;
  if (isAtaDraftDeleted(draft.id, draft.meetingId)) return null;
  const current = listAtaDrafts();
  const idx = current.findIndex(d => d.id === draft.id);
  let saved;
  let next;
  if (idx >= 0) {
    saved = {
      ...current[idx],
      ...draft,
      updatedAt: new Date().toISOString()
    };
    next = current.slice();
    next[idx] = saved;
  } else {
    saved = draft;
    next = [draft, ...current].slice(0, 50);
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  // Fire-and-forget no servidor (demo fica só local).
  // Use persist:false + syncAtaDraftToServer quando for await (ex.: criação).
  if (persist) {
    Promise.resolve(persistDraftToServer(saved)).catch(() => {});
  }
  return saved;
}

export function getAtaDraft(id) {
  return listAtaDrafts().find(d => d.id === id) || null;
}

export function updateAtaDraft(id, patch) {
  if (isAtaDraftDeleted(id, patch && patch.meetingId)) return null;
  const current = listAtaDrafts();
  let updated = null;
  const next = current.map(d => {
    if (d.id !== id) return d;
    if (isAtaDraftDeleted(d.id, d.meetingId)) return d;
    updated = { ...d, ...patch, updatedAt: new Date().toISOString() };
    return updated;
  });
  if (!updated) return null;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  Promise.resolve(persistDraftToServer(updated)).catch(() => {});
  return updated;
}

/** Persiste status resolvido quando o draft ficou inconsistente (ex.: rascunho + IA). */
export function healAtaStatusIfStuck(draftId) {
  const draft = getAtaDraft(draftId);
  if (!draft) return null;
  const resolved = resolveAtaStatus(draft);
  if (resolved === draft.status) return draft;
  const patch = { status: resolved };
  if (resolved !== ATA_STATUS.RASCUNHO && !draft.processedByAi) {
    patch.processedByAi = true;
  }
  return updateAtaDraft(draftId, patch) || draft;
}

export function deleteAtaDraft(id) {
  const current = listAtaDrafts();
  const next = current.filter(d => d.id !== id);
  if (next.length === current.length) return false;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("ata-drafts-updated", { detail: { id, deleted: true } })
    );
  }
  return true;
}

/** Limpa cache local de atas (lab). Não apaga no servidor. */
export function clearAllAtaDraftsLocal() {
  localStorage.removeItem(STORAGE_KEY);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("ata-drafts-updated", { detail: { cleared: true } })
    );
  }
  return true;
}

/** Status de encaminhamento (fechamento completo em etapa posterior). */
export const ACTION_STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  DONE: "done",
  CANCELLED: "cancelled",
  OVERDUE: "overdue"
};

/**
 * Atualiza status de encaminhamentos em atas anteriores (ex.: retorno da família).
 * Aceita match por id; se falhar, tenta por descrição (IDs gerados pela IA).
 * Preserva in_progress / done / cancelled — inclusive parcial obrigatório.
 */
export function applyPriorActionUpdates(updates, excludeDraftId) {
  const list = Array.isArray(updates) ? updates : [];
  if (!list.length) return;
  const normalizeStatus = raw => {
    const s = String(raw || "")
      .trim()
      .toLowerCase();
    if (!s) return null;
    if (s === "done" || s === "concluido" || s === "concluído" || s === "closed") {
      return ACTION_STATUS.DONE;
    }
    if (
      s === "in_progress" ||
      s === "partial" ||
      s === "parcial" ||
      s === "em_andamento" ||
      s === "em andamento"
    ) {
      return ACTION_STATUS.IN_PROGRESS;
    }
    if (s === "cancelled" || s === "canceled" || s === "cancelado") {
      return ACTION_STATUS.CANCELLED;
    }
    if (s === "pending" || s === "pendente" || s === "open") {
      return ACTION_STATUS.PENDING;
    }
    if (Object.values(ACTION_STATUS).includes(s)) return s;
    return null;
  };

  const byId = new Map();
  list.forEach(u => {
    if (!u) return;
    const key = String(u.actionId || u.id || "").trim();
    if (key) byId.set(key, u);
  });

  const descKey = text =>
    String(text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 48);

  listAtaDrafts().forEach(ata => {
    if (!ata || (excludeDraftId && ata.id === excludeDraftId)) return;
    let changed = false;
    const usedUpdateKeys = new Set();
    const actions = (ata.actions || []).map(action => {
      const key = String(action.id || "");
      let u = key && byId.has(key) ? byId.get(key) : null;
      if (!u && action.description) {
        const dk = descKey(action.description);
        u = list.find(item => {
          if (!item) return false;
          const itemKey = String(item.actionId || item.id || "");
          if (itemKey && usedUpdateKeys.has(itemKey)) return false;
          const idDesc = descKey(item.description);
          if (idDesc && dk && (dk.includes(idDesc) || idDesc.includes(dk))) {
            return true;
          }
          return false;
        });
      }
      if (!u) return action;
      const uKey = String(u.actionId || u.id || "");
      if (uKey) usedUpdateKeys.add(uKey);
      const nextStatus = normalizeStatus(u.status) || action.status;
      changed = true;
      return {
        ...action,
        status: nextStatus || action.status,
        statusNote: u.note || u.statusNote || action.statusNote || null,
        closedInFollowUp: true
      };
    });
    if (changed) {
      updateAtaDraft(ata.id, { actions });
    }
  });
}

/**
 * Aplica fechamentos declarados em novos encaminhamentos (closesPriorActionId).
 * Não sobrescreve itens já atualizados como parcial (in_progress) ou com status
 * explícito em priorActionUpdates — o parcial deve prevalecer.
 */
export function applyClosesPriorActionIds(actions, excludeDraftId, opts = {}) {
  const list = Array.isArray(actions) ? actions : [];
  const skip = new Set(
    (opts.skipActionIds || []).map(id => String(id || "")).filter(Boolean)
  );
  const updates = list
    .filter(a => a && a.closesPriorActionId)
    .filter(a => !skip.has(String(a.closesPriorActionId)))
    .map(a => ({
      actionId: a.closesPriorActionId,
      status: ACTION_STATUS.DONE,
      note:
        a.closesPriorNote ||
        i18n.t("smartMinutes.action.closedByFollowUp", {
          defaultValue: "Concluído na reunião de retorno."
        })
    }));
  if (updates.length) {
    applyPriorActionUpdates(updates, excludeDraftId);
  }
}

function flushPriorActionPending(existing, draftId) {
  const patch = {};
  if (Array.isArray(existing.pendingPriorActionReviews)) {
    patch.priorActionReviews = existing.pendingPriorActionReviews;
    patch.pendingPriorActionReviews = null;
  }
  let updated =
    Object.keys(patch).length > 0 ? updateAtaDraft(draftId, patch) : existing;
  if (
    Array.isArray(existing.pendingPriorActionUpdates) &&
    existing.pendingPriorActionUpdates.length
  ) {
    applyPriorActionUpdates(existing.pendingPriorActionUpdates, draftId);
    updated =
      updateAtaDraft(draftId, { pendingPriorActionUpdates: null }) || updated;
  }
  return updated;
}

/**
 * Libera encaminhamentos pendentes para revisão (não exige voicesLinked).
 * Também aplica fechamento dos encaminhamentos da reunião anterior.
 */
export function releasePendingActions(draftId) {
  const existing = getAtaDraft(draftId);
  if (!existing) return null;
  const pending = existing.pendingActions;
  const hasPendingActions = Array.isArray(pending) && pending.length > 0;
  const hasPriorPending =
    (Array.isArray(existing.pendingPriorActionUpdates) &&
      existing.pendingPriorActionUpdates.length > 0) ||
    (Array.isArray(existing.pendingPriorActionReviews) &&
      existing.pendingPriorActionReviews.length > 0);

  if (!hasPendingActions) {
    if (hasPriorPending) {
      const patch = {};
      if (!existing.actionsReady && (existing.actions || []).length) {
        patch.actionsReady = true;
        patch.status =
          existing.status === ATA_STATUS.VOZ_PROCESSADA
            ? ATA_STATUS.PROCESSADA_IA
            : existing.status;
      }
      if (Object.keys(patch).length) {
        updateAtaDraft(draftId, patch);
      }
      const updated = flushPriorActionPending(
        getAtaDraft(draftId) || existing,
        draftId
      );
      if (updated && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ata-drafts-updated", { detail: { id: draftId } })
        );
      }
      return getAtaDraft(draftId) || updated;
    }
    if (existing.actionsReady) return existing;
    // Nada pendente — marca pronto se já há ações (legado)
    if ((existing.actions || []).length) {
      return updateAtaDraft(draftId, {
        actionsReady: true,
        status:
          existing.status === ATA_STATUS.VOZ_PROCESSADA
            ? ATA_STATUS.PROCESSADA_IA
            : existing.status
      });
    }
    return existing;
  }

  const agents = Array.isArray(existing.processAgents)
    ? existing.processAgents
    : [];
  const nextAgents = agents.includes("agent2_extract_actions")
    ? agents
    : [...agents, "agent2_extract_actions"];

  const patch = {
    actions: pending,
    pendingActions: null,
    actionsReady: true,
    status: ATA_STATUS.PROCESSADA_IA,
    processAgents: nextAgents
  };
  if (Array.isArray(existing.pendingPriorActionReviews)) {
    patch.priorActionReviews = existing.pendingPriorActionReviews;
    patch.pendingPriorActionReviews = null;
  }
  const updated = updateAtaDraft(draftId, patch);

  const priorUpdates = Array.isArray(existing.pendingPriorActionUpdates)
    ? existing.pendingPriorActionUpdates
    : [];
  if (priorUpdates.length) {
    applyPriorActionUpdates(priorUpdates, draftId);
    updateAtaDraft(draftId, { pendingPriorActionUpdates: null });
  }
  // closesPrior: só para itens sem update explícito (parcial/done da reunião)
  const skipCloseIds = priorUpdates
    .map(u => u && (u.actionId || u.id))
    .filter(Boolean);
  applyClosesPriorActionIds(
    Array.isArray(pending) ? pending : existing.actions || [],
    draftId,
    { skipActionIds: skipCloseIds }
  );

  if (updated && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("ata-drafts-updated", { detail: { id: draftId } })
    );
  }
  return getAtaDraft(draftId) || updated;
}

function draftMeetingTime(ata) {
  if (!ata) return 0;
  return new Date(ata.meetingAt || ata.createdAt || 0).getTime() || 0;
}

/** Ata criada como retorno (snapshot aponta para reuniões estritamente anteriores). */
function wasCreatedAsFollowUp(ata) {
  const ctx = ata && ata.priorMeetingContext;
  if (!ctx) return false;
  const tDraft = draftMeetingTime(ata);
  const ids = Array.isArray(ctx.priorAtaIds) ? ctx.priorAtaIds : [];
  if (
    ids.some(id => {
      const other = getAtaDraft(id);
      return other && draftMeetingTime(other) < tDraft;
    })
  ) {
    return true;
  }
  const open = Array.isArray(ctx.openActions) ? ctx.openActions : [];
  return open.some(a => {
    if (!a || !a.ataId) return false;
    const other = getAtaDraft(a.ataId);
    return other && draftMeetingTime(other) < tDraft;
  });
}

/** Remove cópias exatas consecutivas/esparsas (descrição + assignee + status). */
export function dedupeIdenticalActions(actions) {
  const list = Array.isArray(actions) ? actions : [];
  const seen = new Map();
  const out = [];
  list.forEach(action => {
    if (!action) return;
    const status =
      String(
        (action && (action.status || action.actionStatus)) || "pending"
      )
        .trim()
        .toLowerCase() || "pending";
    const key = [
      String(action.description || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim(),
      String(action.assignee || "")
        .toLowerCase()
        .trim(),
      status
    ].join("|");
    if (!key || key === "||pending") {
      out.push(action);
      return;
    }
    const prev = seen.get(key);
    if (!prev) {
      seen.set(key, action);
      out.push(action);
      return;
    }
    // Mantém o que tiver nota de status (mais informativo)
    if (!prev.statusNote && action.statusNote) {
      const idx = out.indexOf(prev);
      if (idx >= 0) out[idx] = action;
      seen.set(key, action);
    }
  });
  return out;
}

/**
 * Remove reviews/updates inventados que apontam para a própria ata ou
 * para reuniões posteriores (rastro de sync antigo no lab).
 */
export function scrubInvalidPriorTracking(draftId) {
  healAtaStatusIfStuck(draftId);
  const draft = getAtaDraft(draftId);
  if (!draft) return null;
  const tDraft = draftMeetingTime(draft);
  const isValidOrigin = originId => {
    if (!originId) return false;
    if (String(originId) === String(draftId)) return false;
    const origin = getAtaDraft(originId);
    if (!origin) return true;
    return draftMeetingTime(origin) < tDraft;
  };

  const reviews = Array.isArray(draft.priorActionReviews)
    ? draft.priorActionReviews
    : [];
  const updates = Array.isArray(draft.priorActionUpdates)
    ? draft.priorActionUpdates
    : [];
  const followUp = wasCreatedAsFollowUp(draft);
  const nextReviews = reviews.filter(r => {
    const oid = r && (r.ataId || r.originAtaId);
    // Sem origem: só mantém em ata de retorno (bloco legítimo do job)
    if (!oid) return followUp;
    return isValidOrigin(oid);
  });
  // Updates sem ataId: só mantém se ainda houver review válida do mesmo actionId
  const validReviewIds = new Set(
    nextReviews.map(r => String((r && (r.actionId || r.id)) || "")).filter(Boolean)
  );
  const nextUpdates = updates.filter(u => {
    if (!u) return false;
    const oid = u.ataId || u.originAtaId;
    if (oid) return isValidOrigin(oid);
    const aid = String(u.actionId || u.id || "");
    return !aid || validReviewIds.has(aid) || followUp;
  });

  const patch = {};
  if (nextReviews.length !== reviews.length) {
    patch.priorActionReviews = nextReviews;
  }
  if (nextUpdates.length !== updates.length) {
    patch.priorActionUpdates = nextUpdates;
  }

  // Dedupa encaminhamentos idênticos (mesmo texto+responsável) — rastro de reprocesso
  const actions = Array.isArray(draft.actions) ? draft.actions : [];
  const deduped = dedupeIdenticalActions(actions);
  if (deduped.length !== actions.length) {
    patch.actions = deduped;
  }

  // Limpa priorMeetingContext inventado com atas posteriores / a própria
  const ctx = draft.priorMeetingContext;
  if (ctx) {
    const priorAtaIds = (ctx.priorAtaIds || []).filter(id => {
      if (String(id) === String(draftId)) return false;
      const other = getAtaDraft(id);
      return other && draftMeetingTime(other) < tDraft;
    });
    const openActions = (ctx.openActions || []).filter(a =>
      isValidOrigin(a && a.ataId)
    );
    const priorAtasSummary = (ctx.priorAtasSummary || []).filter(s =>
      priorAtaIds.includes(s && s.id)
    );
    const ctxDirty =
      priorAtaIds.length !== (ctx.priorAtaIds || []).length ||
      openActions.length !== (ctx.openActions || []).length ||
      priorAtasSummary.length !== (ctx.priorAtasSummary || []).length;
    if (ctxDirty) {
      patch.priorMeetingContext =
        priorAtaIds.length || openActions.length
          ? {
              ...ctx,
              priorAtaIds,
              openActions,
              priorAtasSummary
            }
          : null;
    }
  }

  if (!Object.keys(patch).length) return draft;
  return updateAtaDraft(draftId, patch) || draft;
}

/**
 * Libera pendências e, em retorno, reconstrói reviews/updates se a ata
 * já tinha sido processada sem esse bloco (atas antigas no localStorage).
 */
export function ensureEncaminhamentosReleased(draftId) {
  let ata = scrubInvalidPriorTracking(draftId) || getAtaDraft(draftId);
  if (!ata) return null;

  const needsRelease =
    (Array.isArray(ata.pendingActions) && ata.pendingActions.length > 0) ||
    (Array.isArray(ata.pendingPriorActionUpdates) &&
      ata.pendingPriorActionUpdates.length > 0) ||
    (Array.isArray(ata.pendingPriorActionReviews) &&
      ata.pendingPriorActionReviews.length > 0);
  if (needsRelease) {
    ata = releasePendingActions(draftId) || ata;
  }

  const hasReviews =
    Array.isArray(ata.priorActionReviews) && ata.priorActionReviews.length > 0;
  const processed = !!(ata.processedByAi || ata.meetingId || ata.actionsReady);
  // Só inventa/reconcilia reviews em ata que nasceu como retorno.
  // Ata anterior NÃO deve puxar openActions de reuniões posteriores.
  if (!processed || hasReviews || !wasCreatedAsFollowUp(ata)) {
    return getAtaDraft(draftId) || ata;
  }

  const tDraft = draftMeetingTime(ata);
  const onlyOlderOpen = items =>
    (items || []).filter(a => {
      if (!a || a.ataId === draftId) return false;
      const origin = a.ataId ? getAtaDraft(a.ataId) : null;
      const tOrigin = origin
        ? draftMeetingTime(origin)
        : new Date(a.ataMeetingAt || 0).getTime() || 0;
      return tOrigin > 0 && tOrigin < tDraft;
    });

  let openPrior = onlyOlderOpen(
    (ata.priorMeetingContext && ata.priorMeetingContext.openActions) || []
  );
  // Job sem snapshot: reconstrói só a partir de atas estritamente anteriores
  if (!openPrior.length) {
    const ras = (ata.students || []).map(s => s.ra).filter(Boolean);
    if (ras.length) {
      // eslint-disable-next-line no-use-before-define
      const rebuilt = buildPriorMeetingContext(ras);
      const filteredOpen = onlyOlderOpen(rebuilt.openActions);
      const priorAtaIds = (rebuilt.priorAtaIds || []).filter(id => {
        if (id === draftId) return false;
        const other = getAtaDraft(id);
        return other && draftMeetingTime(other) < tDraft;
      });
      if (filteredOpen.length && priorAtaIds.length) {
        const priorMeetingContext = {
          ...rebuilt,
          priorAtaIds,
          priorAtasSummary: (rebuilt.priorAtasSummary || []).filter(s =>
            priorAtaIds.includes(s.id)
          ),
          openActions: filteredOpen
        };
        ata = updateAtaDraft(draftId, { priorMeetingContext }) || {
          ...ata,
          priorMeetingContext
        };
        openPrior = filteredOpen;
      }
    }
  }
  if (!openPrior.length) {
    return getAtaDraft(draftId) || ata;
  }

  // Reconcilia retorno → atas anteriores (parcial incluso).
  // ensureFollowUpPriorActions só enriquece quando faz sentido (Alice/retorno).
  try {
    /* eslint-disable global-require */
    const {
      ensureFollowUpPriorActions
    } = require("./fixtures/mockProcessResult");
    /* eslint-enable global-require */
    const enriched = ensureFollowUpPriorActions(
      {
        actions: ata.actions,
        title: ata.title,
        notes: ata.notes,
        students: ata.students,
        fixtureKey: ata.processFixtureKey,
        provider: ata.processProvider,
        audioFileName: ata.audioFileName,
        priorActionUpdates: ata.priorActionUpdates || ata.pendingPriorActionUpdates,
        priorActionReviews: ata.priorActionReviews || ata.pendingPriorActionReviews
      },
      { ...ata, priorMeetingContext: { ...ata.priorMeetingContext, openActions: openPrior } }
    );
    if (
      Array.isArray(enriched.priorActionUpdates) &&
      enriched.priorActionUpdates.length
    ) {
      applyPriorActionUpdates(enriched.priorActionUpdates, draftId);
      const skipCloseIds = enriched.priorActionUpdates
        .map(u => u && (u.actionId || u.id))
        .filter(Boolean);
      applyClosesPriorActionIds(ata.actions || [], draftId, {
        skipActionIds: skipCloseIds
      });
      ata =
        updateAtaDraft(draftId, {
          priorActionReviews:
            enriched.priorActionReviews || ata.priorActionReviews || [],
          priorActionUpdates: enriched.priorActionUpdates
        }) || ata;
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ata-drafts-updated", { detail: { id: draftId } })
        );
      }
    }
  } catch (e) {
    /* fixture / enrich opcional */
  }

  return getAtaDraft(draftId) || ata;
}

/**
 * Ao abrir uma ata antiga, reaplica fechamentos vindos de reuniões posteriores
 * (done / in_progress parcial) caso ainda não estejam no draft.
 */
export function syncActionStatusesFromLaterMeetings(draftId) {
  scrubInvalidPriorTracking(draftId);
  const draft = getAtaDraft(draftId);
  if (!draft) return null;
  const ras = (draft.students || []).map(s => s.ra).filter(Boolean);
  if (!ras.length) return draft;

  const tDraft = new Date(draft.meetingAt || draft.createdAt || 0).getTime();
  const later = listAtaDrafts().filter(a => {
    if (!a || a.id === draftId) return false;
    const aRas = (a.students || []).map(s => s.ra);
    if (!ras.some(r => aRas.includes(r))) return false;
    const tA = new Date(a.meetingAt || a.createdAt || 0).getTime();
    return tA >= tDraft;
  });

  const updates = [];
  later.forEach(a => {
    // Prefer reviews/updates já materializados na ata de retorno
    const fromReviews = a.priorActionReviews || [];
    fromReviews.forEach(r => {
      if (!r || !(r.actionId || r.id)) return;
      updates.push({
        actionId: r.actionId || r.id,
        status: r.status,
        note: r.note || r.statusNote || null,
        description: r.description || null
      });
    });
    const fromUpdates = a.priorActionUpdates || a.pendingPriorActionUpdates || [];
    fromUpdates.forEach(u => {
      if (!u || !(u.actionId || u.id)) return;
      updates.push({
        actionId: u.actionId || u.id,
        status: u.status,
        note: u.note || null,
        description: u.description || null
      });
    });
  });

  if (updates.length) {
    applyPriorActionUpdates(updates, null);
  }

  // Se ainda há atas de retorno sem reviews, tenta enrich nelas
  later.forEach(a => {
    if (
      a.priorMeetingContext &&
      (a.priorMeetingContext.openActions || []).length &&
      !(a.priorActionReviews || []).length
    ) {
      ensureEncaminhamentosReleased(a.id);
    }
  });

  return getAtaDraft(draftId) || draft;
}

/** Painel de encaminhamentos liberado? */
export function canShowEncaminhamentos(ata) {
  if (!ata) return false;
  if (ata.actionsReady) return true;
  if (ata.status === ATA_STATUS.APROVADA) return true;
  if (ata.status === ATA_STATUS.PROCESSADA_IA && (ata.actions || []).length) {
    return true;
  }
  // Legado: ações já gravadas sem o fluxo novo
  if ((ata.actions || []).length > 0 && !Array.isArray(ata.pendingActions)) {
    return true;
  }
  return false;
}

/** Valida a ata (aprovação) — libera encaminhamentos para atas posteriores. */
export function approveAtaDraft(draftId) {
  const existing = getAtaDraft(draftId);
  if (!existing) return null;
  const patch = {
    status: ATA_STATUS.APROVADA,
    approvedAt: new Date().toISOString(),
    actionsValidated: true
  };
  // Garante que encaminhamentos estejam liberados na própria ata
  if ((existing.actions || []).length || (existing.pendingActions || []).length) {
    patch.actionsReady = true;
    if (
      Array.isArray(existing.pendingActions) &&
      existing.pendingActions.length &&
      !(existing.actions || []).length
    ) {
      patch.actions = existing.pendingActions;
      patch.pendingActions = null;
    }
  }
  const updated = updateAtaDraft(draftId, patch);
  if (updated && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("ata-drafts-updated", { detail: { id: draftId } })
    );
  }
  return updated;
}

/** Ata elegível para aprovação (processada / com conteúdo útil). */
export function canApproveAtaDraft(ata) {
  if (!ata || ata.status === ATA_STATUS.APROVADA) return false;
  return (
    ata.status === ATA_STATUS.PROCESSADA_IA ||
    ata.status === ATA_STATUS.VOZ_PROCESSADA ||
    ata.processedByAi === true ||
    !!(ata.transcriptText && String(ata.transcriptText).trim()) ||
    (ata.actions || []).length > 0
  );
}

function normalizeLooseName(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * Participantes da ata agrupados: família (aluno + responsáveis) × escola (staff).
 */
export function groupAtaParticipants(ata, t) {
  const translate = (key, fallback) => {
    if (typeof t === "function") {
      const v = t(key);
      if (v && v !== key) return v;
    }
    const fromI18n = i18n.t(key);
    if (fromI18n && fromI18n !== key) return fromI18n;
    return fallback || key;
  };
  const family = [];
  const school = [];
  const seen = new Set();

  const push = (bucket, row) => {
    if (!row || !row.name) return;
    const key = `${row.side}:${normalizeLooseName(row.name)}`;
    if (seen.has(key)) return;
    seen.add(key);
    bucket.push(row);
  };

  (ata && ata.students ? ata.students : []).forEach(s => {
    if (!s) return;
    let presence = null;
    if (s.present === true) {
      presence = translate("smartMinutes.presence.present", "aluno presente");
    } else if (s.present === false) {
      presence = translate("smartMinutes.presence.absent", "aluno ausente");
    }
    push(family, {
      id: `student:${s.ra || s.name}`,
      name: s.name,
      meta: presence,
      side: "family"
    });
    (s.guardians || []).forEach(g => {
      if (!g || !g.name) return;
      if (normalizeLooseName(g.name) === normalizeLooseName(s.name)) return;
      const roleRaw = g.role || g.relationship || "";
      const role = roleRaw
        ? translate(
            `smartMinutes.role.${String(roleRaw).toLowerCase()}`,
            roleRaw
          )
        : "";
      const respKey = g.responsibility
        ? String(g.responsibility).toLowerCase()
        : "";
      const resp = respKey
        ? translate(`smartMinutes.role.${respKey}`, g.responsibility)
        : null;
      push(family, {
        id: `guardian:${g.id || g.name}`,
        name: g.name,
        meta: [role, resp].filter(Boolean).join(" · ") || null,
        side: "family"
      });
    });
  });

  (ata && ata.staff ? ata.staff : []).forEach(s => {
    if (!s || !s.name) return;
    const role = s.role
      ? translate(
          `smartMinutes.role.${String(s.role).toLowerCase()}`,
          s.role
        )
      : translate("smartMinutes.role.staff", "Funcionário(a)");
    push(school, {
      id: `staff:${s.id || s.name}`,
      name: s.name,
      meta: role,
      side: "school"
    });
  });

  return {
    total: family.length + school.length,
    family,
    school
  };
}

/**
 * Resumo estruturado de alunos/responsáveis (sem repetir o mesmo nome N vezes).
 * Retorna linhas prontas para UI: { primary, secondary }.
 */
export function formatAtaParticipantLines(ata, t) {
  const translate = (key, fallback) => {
    if (typeof t === "function") {
      const v = t(key);
      if (v && v !== key) return v;
    }
    const fromI18n = i18n.t(key);
    if (fromI18n && fromI18n !== key) return fromI18n;
    return fallback || key;
  };
  const raw = (ata && ata.students) || [];
  // Deduplica RA/nome — evita “Alice · Alice · Alice” no hero/lista
  const seen = new Set();
  const students = raw.filter(s => {
    if (!s) return false;
    const key = String(s.ra || normalizeLooseName(s.name) || "").trim();
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (!students.length) {
    return [
      {
        primary: translate("smartMinutes.noStudents", "Sem alunos"),
        secondary: null
      }
    ];
  }
  return students.map(s => {
    let presence = null;
    if (s.present === true) {
      presence = translate("smartMinutes.presence.present", "aluno presente");
    } else if (s.present === false) {
      presence = translate("smartMinutes.presence.absent", "aluno ausente");
    }
    const guardians = Array.isArray(s.guardians) ? s.guardians : [];
    const guardianParts = guardians
      .map(g => {
        if (!g) return null;
        if (typeof g === "string") {
          const text = g.trim();
          if (!text) return null;
          if (normalizeLooseName(text) === normalizeLooseName(s.name)) {
            return null;
          }
          return text;
        }
        const name = String(g.name || "").trim();
        if (!name) return null;
        if (normalizeLooseName(name) === normalizeLooseName(s.name)) {
          return null;
        }
        const roleRaw = g.role || g.relationship || "";
        const role = roleRaw
          ? translate(`smartMinutes.role.${String(roleRaw).toLowerCase()}`, roleRaw)
          : "";
        const respKey = g.responsibility
          ? String(g.responsibility).toLowerCase()
          : "";
        const resp = respKey
          ? translate(`smartMinutes.role.${respKey}`, g.responsibility)
          : null;
        const bits = [role, resp].filter(Boolean);
        return bits.length ? `${bits.join(" · ")}: ${name}` : name;
      })
      .filter(Boolean);
    // guardiansLabel legado — evita repetir o nome do aluno no meio do texto
    if (!guardianParts.length && s.guardiansLabel) {
      const studentKey = normalizeLooseName(s.name);
      String(s.guardiansLabel)
        .split(/[;|]/)
        .map(x => x.trim())
        .filter(Boolean)
        .forEach(part => {
          const partKey = normalizeLooseName(part);
          if (partKey === studentKey) return;
          if (partKey.startsWith(`${studentKey} `) && !/resp|pai|mãe|mae|financ|pedag/i.test(part)) {
            return;
          }
          guardianParts.push(part);
        });
    }
    const secondary = [presence, ...guardianParts].filter(Boolean).join(" · ");
    return {
      primary: s.name || translate("smartMinutes.student", "Aluno"),
      secondary: secondary || null
    };
  });
}

/**
 * Aplica resposta do job process_smart_minutes (microserviço ou demo mock).
 * Transcrição + falantes; encaminhamentos (e fechamento dos anteriores) liberados
 * automaticamente — sem botão "Gerar encaminhamentos".
 */
export function applySmartMinutesJobResult(job) {
  let response = (job && job.response) || {};
  const draftId =
    response.draftId ||
    (job && job.draftId) ||
    (job && job.payload && job.payload.draftId);
  if (!draftId) return null;
  if (job.status !== "success") return null;
  if (
    !response.transcriptText &&
    !(response.actions && response.actions.length)
  ) {
    return null;
  }

  const existing = getAtaDraft(draftId);
  if (!existing) return null;
  if (existing.processAppliedJobId && existing.processAppliedJobId === job.id) {
    return ensureEncaminhamentosReleased(draftId) || existing;
  }
  if (
    existing.meetingId &&
    response.transcriptText &&
    existing.transcriptText === response.transcriptText
  ) {
    return ensureEncaminhamentosReleased(draftId) || existing;
  }

  // Retorno com ata anterior: garante priorActionUpdates (inclui parcial)
  // mesmo quando o pipeline real não devolveu o bloco (lab Alice / título retorno).
  const hasPriorOpen =
    existing.priorMeetingContext &&
    Array.isArray(existing.priorMeetingContext.openActions) &&
    existing.priorMeetingContext.openActions.length > 0;
  if (hasPriorOpen || allowAliceFixtureHelpers(response)) {
    try {
      /* eslint-disable global-require */
      const {
        ensureFollowUpPriorActions
      } = require("./fixtures/mockProcessResult");
      /* eslint-enable global-require */
      response = ensureFollowUpPriorActions(response, existing);
    } catch (e) {
      /* enrich opcional */
    }
  }

  const transcriptText = response.transcriptText
    ? normalizeAttributedTranscript(response.transcriptText, {
        knownNames: listPersonOptions(existing).map(o => o.name)
      })
    : null;

  const agentsIn = Array.isArray(response.agents) ? response.agents : [];
  const agentsPhase1 = agentsIn.filter(a => a !== "agent2_extract_actions");
  const usedMockPipeline =
    response.provider === "mock" || Boolean(response.fixtureKey);
  if (usedMockPipeline) {
    if (!agentsPhase1.includes("stt_mock") && !agentsPhase1.includes("stt")) {
      agentsPhase1.unshift("stt_mock");
    }
    if (!agentsPhase1.includes("diarize_mock")) {
      agentsPhase1.push("diarize_mock");
    }
  } else if (!agentsPhase1.length) {
    agentsPhase1.push("stt", "attribute_speakers");
  }

  const diarizedSegments = Array.isArray(response.diarizedSegments)
    ? response.diarizedSegments
    : null;

  const patch = {
    status: ATA_STATUS.VOZ_PROCESSADA,
    processedByAi: true,
    processAppliedJobId: job.id,
    meetingId: response.meetingId || null,
    transcriptText,
    summary: response.summary || null,
    structuredMinutes: response.structuredMinutes || null,
    tags: response.tags || null,
    processProvider: response.provider || null,
    processFixtureKey: response.fixtureKey || null,
    processAgents: agentsPhase1,
    processPhase: "voice_ready",
    actions: [],
    actionsReady: false,
    voicesLinked: false,
    diarizedSegments,
    diarizeModel: response.diarizeModel || null
  };

  if (Array.isArray(response.actions) && response.actions.length) {
    patch.pendingActions = response.actions;
  }
  if (Array.isArray(response.priorActionReviews)) {
    patch.pendingPriorActionReviews = response.priorActionReviews;
    patch.priorActionReviews = [];
  }
  if (Array.isArray(response.priorActionUpdates)) {
    patch.pendingPriorActionUpdates = response.priorActionUpdates;
  }

  const merged = { ...existing, ...patch };
  let speakers = buildSpeakersFromAta(merged);
  speakers = attachMockVoiceSamples(
    speakers,
    transcriptText || "",
    diarizedSegments
  );
  patch.speakers = speakers;
  patch.voicesLinked =
    speakers.length > 0 &&
    speakers.every(s => s.personId && s.linkSource === "manual");

  const updated = updateAtaDraft(draftId, patch);

  // Libera encaminhamentos (+ fecha anteriores) sem depender de validar falas
  if (
    updated &&
    ((updated.pendingActions && updated.pendingActions.length) ||
      (updated.pendingPriorActionUpdates &&
        updated.pendingPriorActionUpdates.length) ||
      (updated.pendingPriorActionReviews &&
        updated.pendingPriorActionReviews.length))
  ) {
    return releasePendingActions(draftId);
  }

  if (updated && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("ata-drafts-updated", { detail: { id: draftId } })
    );
  }
  return updated;
}

/**
 * Registra amostras (≥3) e aplica sugestões de voz nos speakers (previsão).
 */
export async function syncVoiceSamplesAndSuggestions(ata, smartMinutes) {
  if (!ata || !ata.meetingId || !smartMinutes) return ata;
  const speakers = ata.speakers || [];
  if (!speakers.length) return ata;

  const clusters = speakers.map(s => {
    const samples = Array.isArray(s.voiceSamples) ? s.voiceSamples : [];
    const first = samples.find(
      v => v && !v.excluded && Number.isFinite(v.startSec)
    );
    return {
      speakerClusterId: s.id,
      voiceKey: s.detectedName || s.label || s.id,
      sampleCount: Math.max(
        MIN_VOICE_SAMPLES,
        Number(s.sampleCount) || MIN_VOICE_SAMPLES
      ),
      startSec: (() => {
        if (Number.isFinite(s.sampleStartSec)) return s.sampleStartSec;
        if (first) return first.startSec;
        return null;
      })(),
      endSec: (() => {
        if (Number.isFinite(s.sampleEndSec)) return s.sampleEndSec;
        if (first && Number.isFinite(first.endSec)) return first.endSec;
        return null;
      })(),
      personKey: s.personId || null
    };
  });

  try {
    await smartMinutes.registerVoiceSamples({
      meetingId: ata.meetingId,
      clusters
    });
  } catch (e) {
    // register samples: best-effort
  }

  const hintClusters = speakers.map(s => ({
    speakerClusterId: s.id,
    voiceKey: s.detectedName || s.label || s.id,
    // Hint só para rerank leve na API — query usa amostras
    personKeyHint: s.suggestedPersonId || null
  }));

  let suggest;
  try {
    suggest = await smartMinutes.suggestSpeakersByVoice({
      meetingId: ata.meetingId,
      clusters: hintClusters,
      limit: 3
    });
  } catch (e) {
    return ata;
  }

  const byCluster = new Map(
    ((suggest && suggest.results) || []).map(r => [r.speakerClusterId, r])
  );
  let changed = false;
  const nextSpeakers = speakers.map(s => {
    if (s.linkSource === "manual" && s.personId) return s;
    const row = byCluster.get(s.id);
    const best =
      row && row.matches && row.matches.find(m => Number(m.score) >= 0.7);
    if (!best) return s;
    changed = true;
    return {
      ...s,
      suggestedPersonId: best.personKey,
      suggestedPersonName: best.displayName,
      suggestedPersonRole: s.suggestedPersonRole || s.detectedRole,
      linkSource: "suggestion",
      voiceSuggestScore: Number(best.score),
      voiceSuggestSource:
        best.scoreSource || row.scoreSource || "sample_embedding"
    };
  });

  if (!changed) return ata;
  return updateAtaDraft(ata.id, { speakers: nextSpeakers });
}

export function createDraftId() {
  return `ata_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/**
 * Remove atas de seed demo antigas do localStorage (uma vez por navegador).
 */
export function purgeDemoAtas() {
  try {
    if (localStorage.getItem(DEMO_PURGED_KEY) === "1") {
      return listAtaDrafts();
    }
    const cleaned = listAtaDrafts().filter(a => !isDemoAtaId(a.id));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
    localStorage.setItem(DEMO_PURGED_KEY, "1");
    return cleaned;
  } catch (e) {
    return listAtaDrafts();
  }
}

export function downloadTextFile(filename, content, mime = "text/plain") {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Baixa o áudio (IndexedDB → S3/proxy); senão placeholder texto (demo legado).
 */
export async function downloadAudioPlaceholder(ata) {
  try {
    const { downloadAtaAudioBlob } = await import("./ataAudioStore");
    const downloaded = await downloadAtaAudioBlob(ata);
    if (downloaded) return { kind: "audio" };
  } catch (e) {
    // Áudio indisponível — segue com placeholder texto.
  }

  const name = (ata.audioFileName || `ata-${ata.id}.txt`).replace(
    /\.(mp3|wav|m4a|ogg)$/i,
    ".txt"
  );
  const content = [
    i18n.t("smartMinutes.audio.placeholder"),
    `${i18n.t("common.labels.title")}: ${ata.title}`,
    i18n.t("smartMinutes.download.originalFile", {
      name: ata.audioFileName || i18n.t("common.messages.notInformed")
    }),
    `${i18n.t("common.labels.date")}: ${ata.createdAt}`,
    "",
    i18n.t("smartMinutes.audio.realDownloadNote")
  ].join("\n");
  downloadTextFile(name, content);
  return { kind: "placeholder" };
}

export function downloadTranscript(ata) {
  const content =
    ata.transcriptText ||
    [
      i18n.t("smartMinutes.download.transcriptTitle", { title: ata.title }),
      i18n.t("smartMinutes.download.statusLine", {
        status: getAtaStatusLabel(ata.status) || ata.status
      }),
      i18n.t("smartMinutes.download.dateLine", { date: ata.createdAt }),
      "",
      ...(ata.students || []).map(s => {
        let presence = null;
        if (s.present === true) {
          presence = i18n.t("smartMinutes.presence.present");
        } else if (s.present === false) {
          presence = i18n.t("smartMinutes.presence.absent");
        }
        const extras = [
          presence
            ? i18n.t("smartMinutes.download.studentPresence", { presence })
            : null,
          s.guardiansLabel || null
        ]
          .filter(Boolean)
          .join(" · ");
        return `${i18n.t("smartMinutes.download.studentLine", {
          name: s.name,
          ra: s.ra
        })}${extras ? ` · ${extras}` : ""}`;
      }),
      "",
      ata.notes
        ? i18n.t("smartMinutes.download.agendaLine", { notes: ata.notes })
        : "",
      "",
      ...(ata.actions || []).map(
        (action, idx) =>
          `${i18n.t("smartMinutes.list.actions")} ${idx + 1}: ${
            action.description
          }${action.assignee ? ` — ${action.assignee}` : ""}`
      ),
      "",
      i18n.t("smartMinutes.download.pendingFull")
    ]
      .filter(Boolean)
      .join("\n");

  downloadTextFile(`transcricao-${ata.id}.txt`, content);
}

/**
 * Encaminhamentos sugeridos a partir do formulário (placeholder até a API de IA).
 */
export function buildSuggestedActions({
  notes,
  students,
  staff,
  processedByAi
}) {
  const actions = [];
  const operator =
    (staff || []).find(s => /operador/i.test(s.role || "")) ||
    (staff || [])[0] ||
    null;
  const coord =
    (staff || []).find(s => /coordena/i.test(s.role || "")) || operator;

  (students || []).forEach(student => {
    const family = student.guardiansLabel || "a família";
    actions.push({
      id: `act_${student.ra}_retorno`,
      description: processedByAi
        ? `Retornar à família (${family}) sobre o acompanhamento de ${student.name}`
        : `Registrar combinados com ${family} referentes a ${student.name}`,
      assignee: (coord && coord.name) || "Coordenação",
      dueHint: "próxima semana",
      priority: "medium",
      studentRa: student.ra,
      needsFamilyReturn: true
    });
  });

  if (notes && notes.trim()) {
    actions.push({
      id: `act_pauta_${Date.now()}`,
      description: `Cumprir pauta registrada: ${notes.trim().slice(0, 160)}`,
      assignee: (operator && operator.name) || "Operador",
      dueHint: null,
      priority: "medium",
      needsFamilyReturn: false
    });
  }

  if (processedByAi && !(students || []).length) {
    actions.push({
      id: `act_revisar_${Date.now()}`,
      description:
        "Revisar encaminhamentos sugeridos pela IA e validar responsáveis",
      assignee: (operator && operator.name) || "Operador",
      priority: "high"
    });
  }

  return actions;
}

export function filterAtasForContext(atas, { classId, studentRa } = {}) {
  return atas.filter(a => {
    if (isDemoAtaId(a.id)) return false;
    if (studentRa) {
      return (a.students || []).some(s => String(s.ra) === String(studentRa));
    }
    if (classId) {
      return !a.classId || String(a.classId) === String(classId);
    }
    return true;
  });
}

/** Status de encaminhamento — labels i18n (const ACTION_STATUS definido acima). */
const ACTION_STATUS_I18N = {
  [ACTION_STATUS.PENDING]: "smartMinutes.action.pending",
  [ACTION_STATUS.IN_PROGRESS]: "smartMinutes.action.inProgress",
  [ACTION_STATUS.DONE]: "smartMinutes.action.done",
  [ACTION_STATUS.CANCELLED]: "smartMinutes.action.cancelled",
  [ACTION_STATUS.OVERDUE]: "smartMinutes.action.overdue"
};

/** @deprecated use getActionStatusLabel */
export const ACTION_STATUS_LABEL = {};

export function getActionStatusLabel(status) {
  const key = ACTION_STATUS_I18N[status];
  return key ? i18n.t(key) : status;
}

export function getActionStatus(action) {
  const raw = String(
    (action && action.status) || ACTION_STATUS.PENDING
  ).toLowerCase();
  if (Object.values(ACTION_STATUS).includes(raw)) return raw;
  return ACTION_STATUS.PENDING;
}

export function isActionOpen(action) {
  const status = getActionStatus(action);
  return status !== ACTION_STATUS.DONE && status !== ACTION_STATUS.CANCELLED;
}

/** Filtros globais de encaminhamentos (nova ata + detalhe). */
export const ACTIONS_FILTER = {
  ALL: "all",
  OPEN: "open",
  DONE: "done",
  CANCELLED: "cancelled"
};

export function matchesActionsFilter(action, filter) {
  const status = getActionStatus(action);
  if (filter === ACTIONS_FILTER.OPEN) return isActionOpen(action);
  if (filter === ACTIONS_FILTER.DONE) return status === ACTION_STATUS.DONE;
  if (filter === ACTIONS_FILTER.CANCELLED) {
    return status === ACTION_STATUS.CANCELLED;
  }
  return true;
}

export function filterActionsByStatus(actions, filter) {
  return (actions || []).filter(a => matchesActionsFilter(a, filter));
}

export function countActionsByFilter(actions) {
  const list = actions || [];
  return {
    [ACTIONS_FILTER.ALL]: list.length,
    [ACTIONS_FILTER.OPEN]: list.filter(isActionOpen).length,
    [ACTIONS_FILTER.DONE]: list.filter(
      a => getActionStatus(a) === ACTION_STATUS.DONE
    ).length,
    [ACTIONS_FILTER.CANCELLED]: list.filter(
      a => getActionStatus(a) === ACTION_STATUS.CANCELLED
    ).length
  };
}

/**
 * Aplica edição/remoção/adição de uma lista filtrada sem perder itens ocultos.
 * Reordenação completa só é fiel com filtro "Todos".
 */
export function mergeFilteredActionEdits(allActions, nextVisible, filter) {
  const all = Array.isArray(allActions) ? allActions : [];
  const visible = Array.isArray(nextVisible) ? nextVisible : [];
  if (!filter || filter === ACTIONS_FILTER.ALL) return visible;

  const nextById = new Map(
    visible.filter(a => a && a.id).map(a => [String(a.id), a])
  );
  const wasVisibleIds = new Set(
    all.filter(a => matchesActionsFilter(a, filter)).map(a => String(a.id))
  );
  const removed = new Set([...wasVisibleIds].filter(id => !nextById.has(id)));
  const allIds = new Set(all.map(a => String(a.id)));
  const merged = all
    .filter(a => !removed.has(String(a.id)))
    .map(a => nextById.get(String(a.id)) || a);
  const brandNew = visible.filter(a => a && a.id && !allIds.has(String(a.id)));
  return [...merged, ...brandNew];
}

/**
 * Atas anteriores dos alunos selecionados (histórico na nova reunião).
 * Ordena da mais recente para a mais antiga.
 */
export function listPriorAtasForStudents(ras, { excludeId } = {}) {
  const set = new Set((ras || []).map(r => String(r)));
  if (!set.size) return [];
  return listAtaDrafts()
    .filter(a => {
      if (isDemoAtaId(a.id)) return false;
      if (excludeId && a.id === excludeId) return false;
      return (a.students || []).some(s => set.has(String(s.ra)));
    })
    .sort((a, b) => {
      const da = new Date(b.meetingAt || b.createdAt || 0).getTime();
      const db = new Date(a.meetingAt || a.createdAt || 0).getTime();
      return da - db;
    });
}

/**
 * Encaminhamentos das atas anteriores, com vínculo à ata de origem.
 * Por padrão só os abertos (pendente / em andamento / atrasado).
 */
export function collectActionsFromAtas(atas, { openOnly = true } = {}) {
  const items = [];
  (atas || []).forEach(ata => {
    (ata.actions || []).forEach((action, idx) => {
      if (openOnly && !isActionOpen(action)) return;
      items.push({
        ...action,
        id: action.id || `${ata.id}_act_${idx}`,
        status: getActionStatus(action),
        ataId: ata.id,
        ataTitle: ata.title || i18n.t("smartMinutes.ata"),
        ataMeetingAt: ata.meetingAt || ata.createdAt || null,
        studentRa: action.studentRa || null,
        studentName:
          action.studentName ||
          (
            (ata.students || []).find(
              s => String(s.ra) === String(action.studentRa)
            ) ||
            (ata.students || [])[0] ||
            {}
          ).name ||
          null
      });
    });
  });
  return items;
}

/**
 * Ata cujos encaminhamentos entram no histórico da próxima reunião.
 * Preferência: aprovada; também processada com ações liberadas (fluxo de lab/retorno).
 */
export function ataContributesPriorActions(ata) {
  if (!ata) return false;
  if (ata.status === ATA_STATUS.APROVADA) return true;
  if (!canShowEncaminhamentos(ata)) return false;
  return (ata.actions || []).length > 0;
}

/**
 * Snapshot leve para gravar na nova ata (contexto histórico + encaminhamentos abertos).
 * Atas aprovadas (e processadas com encaminhamentos) alimentam o retorno.
 */
export function buildPriorMeetingContext(ras) {
  const priorAtas = listPriorAtasForStudents(ras).filter(ataContributesPriorActions);
  const openActions = collectActionsFromAtas(priorAtas, { openOnly: true });
  return {
    capturedAt: new Date().toISOString(),
    priorAtaIds: priorAtas.map(a => a.id),
    priorAtasSummary: priorAtas.map(a => ({
      id: a.id,
      title: a.title || "Ata",
      meetingAt: a.meetingAt || a.createdAt || null,
      status: a.status,
      studentRas: (a.students || []).map(s => s.ra)
    })),
    openActions: openActions.map(a => ({
      id: a.id,
      description: a.description,
      assignee: a.assignee || null,
      dueHint: a.dueHint || null,
      status: a.status,
      ataId: a.ataId,
      ataTitle: a.ataTitle,
      studentRa: a.studentRa,
      studentName: a.studentName
    }))
  };
}

export function searchAtas(atas, query) {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return atas;

  return atas.filter(a => {
    const haystack = [
      a.title,
      a.notes,
      a.audioFileName,
      a.status,
      getAtaStatusLabel(a.status),
      ...(a.students || []).flatMap(s => [
        s.name,
        s.ra,
        s.guardiansLabel,
        ...(s.guardians || []).map(g =>
          typeof g === "string" ? g : `${g.role || ""} ${g.name || ""}`
        )
      ]),
      ...(a.staff || []).flatMap(s => [s.name, s.role]),
      ...(a.actions || []).flatMap(act => [
        act.description,
        act.assignee,
        act.dueHint
      ])
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(q);
  });
}
